import { useCallback, useEffect, useRef } from 'react';
import { 
  getGlobalAnnouncer, 
  type AnnouncementOptions,
  type AnnouncementMessage 
} from '@/lib/screen-reader';

/**
 * Enhanced hook for screen reader announcements
 * Provides comprehensive announcement capabilities with queuing and priority handling
 */
export const useScreenReader = () => {
  const announcerRef = useRef(getGlobalAnnouncer());

  // Basic announcement function
  const announce = useCallback((message: string, options?: AnnouncementOptions): string => {
    return announcerRef.current.announce(message, options);
  }, []);

  // Convenience methods for different message types
  const announceSuccess = useCallback((message: string, options?: Omit<AnnouncementOptions, 'type'>): string => {
    return announcerRef.current.announceSuccess(message, options);
  }, []);

  const announceError = useCallback((message: string, options?: Omit<AnnouncementOptions, 'type'>): string => {
    return announcerRef.current.announceError(message, options);
  }, []);

  const announceWarning = useCallback((message: string, options?: Omit<AnnouncementOptions, 'type'>): string => {
    return announcerRef.current.announceWarning(message, options);
  }, []);

  const announceStatus = useCallback((message: string, options?: Omit<AnnouncementOptions, 'type'>): string => {
    return announcerRef.current.announceStatus(message, options);
  }, []);

  const announceProgress = useCallback((message: string, options?: Omit<AnnouncementOptions, 'type'>): string => {
    return announcerRef.current.announceProgress(message, options);
  }, []);

  // Queue management
  const cancel = useCallback((id: string): boolean => {
    return announcerRef.current.cancel(id);
  }, []);

  const clear = useCallback((): void => {
    announcerRef.current.clear();
  }, []);

  const clearQueue = useCallback((priority: 'polite' | 'assertive'): void => {
    announcerRef.current.clearQueue(priority);
  }, []);

  // Information methods
  const getHistory = useCallback((limit?: number): AnnouncementMessage[] => {
    return announcerRef.current.getHistory(limit);
  }, []);

  const getQueueStatus = useCallback(() => {
    return announcerRef.current.getQueueStatus();
  }, []);

  return {
    // Core announcement methods
    announce,
    announceSuccess,
    announceError,
    announceWarning,
    announceStatus,
    announceProgress,
    
    // Queue management
    cancel,
    clear,
    clearQueue,
    
    // Information
    getHistory,
    getQueueStatus,
  };
};

/**
 * Hook for form-specific screen reader announcements
 */
export const useFormScreenReader = () => {
  const { announce, announceError, announceSuccess } = useScreenReader();

  const announceFieldError = useCallback((fieldName: string, error: string): string => {
    return announceError(`${fieldName}: ${error}`, { 
      priority: 'assertive',
      interrupt: true 
    });
  }, [announceError]);

  const announceFieldSuccess = useCallback((fieldName: string, message?: string): string => {
    const successMessage = message || `${fieldName} is valid`;
    return announceSuccess(successMessage);
  }, [announceSuccess]);

  const announceFormSubmission = useCallback((status: 'submitting' | 'success' | 'error', message?: string): string => {
    switch (status) {
      case 'submitting':
        return announce('Form is being submitted', { 
          type: 'status',
          priority: 'polite' 
        });
      case 'success':
        return announceSuccess(message || 'Form submitted successfully');
      case 'error':
        return announceError(message || 'Form submission failed');
      default:
        return '';
    }
  }, [announce, announceSuccess, announceError]);

  const announceValidationSummary = useCallback((errors: string[]): string => {
    if (errors.length === 0) {
      return announceSuccess('All fields are valid');
    }
    
    const errorCount = errors.length;
    const summary = `Form has ${errorCount} error${errorCount > 1 ? 's' : ''}: ${errors.join(', ')}`;
    return announceError(summary, { priority: 'assertive' });
  }, [announceSuccess, announceError]);

  return {
    announceFieldError,
    announceFieldSuccess,
    announceFormSubmission,
    announceValidationSummary,
  };
};

/**
 * Hook for navigation-specific screen reader announcements
 */
export const useNavigationScreenReader = () => {
  const { announce, announceStatus } = useScreenReader();

  const announcePageChange = useCallback((pageName: string): string => {
    return announceStatus(`Navigated to ${pageName}`, { 
      priority: 'polite',
      timeout: 3000 
    });
  }, [announceStatus]);

  const announceRouteChange = useCallback((from: string, to: string): string => {
    return announce(`Navigating from ${from} to ${to}`, { 
      type: 'status',
      priority: 'polite' 
    });
  }, [announce]);

  const announceModalOpen = useCallback((modalTitle: string): string => {
    return announceStatus(`${modalTitle} dialog opened`, { 
      priority: 'polite' 
    });
  }, [announceStatus]);

  const announceModalClose = useCallback((modalTitle?: string): string => {
    const message = modalTitle ? `${modalTitle} dialog closed` : 'Dialog closed';
    return announceStatus(message, { priority: 'polite' });
  }, [announceStatus]);

  const announceMenuOpen = useCallback((menuName: string): string => {
    return announceStatus(`${menuName} menu opened`, { 
      priority: 'polite' 
    });
  }, [announceStatus]);

  const announceMenuClose = useCallback((menuName?: string): string => {
    const message = menuName ? `${menuName} menu closed` : 'Menu closed';
    return announceStatus(message, { priority: 'polite' });
  }, [announceStatus]);

  return {
    announcePageChange,
    announceRouteChange,
    announceModalOpen,
    announceModalClose,
    announceMenuOpen,
    announceMenuClose,
  };
};

/**
 * Hook for loading and progress announcements
 */
export const useLoadingScreenReader = () => {
  const { announceProgress, announceStatus, announceSuccess, announceError } = useScreenReader();
  const loadingAnnouncementRef = useRef<string | null>(null);

  const announceLoadingStart = useCallback((message: string = 'Loading'): string => {
    const id = announceStatus(`${message}...`, { 
      priority: 'polite',
      persistent: true 
    });
    loadingAnnouncementRef.current = id;
    return id;
  }, [announceStatus]);

  const announceLoadingProgress = useCallback((progress: number, total?: number): string => {
    const message = total 
      ? `Loading progress: ${progress} of ${total}`
      : `Loading progress: ${progress}%`;
    
    return announceProgress(message, { 
      priority: 'polite',
      timeout: 2000 
    });
  }, [announceProgress]);

  const announceLoadingComplete = useCallback((message: string = 'Loading complete'): string => {
    // Cancel the loading announcement if it exists
    if (loadingAnnouncementRef.current) {
      // Note: We don't cancel here as the completion message is more important
      loadingAnnouncementRef.current = null;
    }
    
    return announceSuccess(message, { priority: 'polite' });
  }, [announceSuccess]);

  const announceLoadingError = useCallback((message: string = 'Loading failed'): string => {
    // Cancel the loading announcement if it exists
    if (loadingAnnouncementRef.current) {
      loadingAnnouncementRef.current = null;
    }
    
    return announceError(message, { 
      priority: 'assertive',
      interrupt: true 
    });
  }, [announceError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loadingAnnouncementRef.current) {
        // The global announcer will handle cleanup
        loadingAnnouncementRef.current = null;
      }
    };
  }, []);

  return {
    announceLoadingStart,
    announceLoadingProgress,
    announceLoadingComplete,
    announceLoadingError,
  };
};